import { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, SkipBack, SkipForward, Repeat, Heart, BookOpen, ChevronDown, MoreHorizontal } from 'lucide-react';
import { PiShareFatFill } from 'react-icons/pi';
import { cn } from '@/lib/utils';
import { hapticLight, hapticMedium } from '@/lib/haptics';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRoxboxPlayerContext, type RoxboxTrack } from '@/contexts/RoxboxPlayerContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useColorExtraction } from '@/hooks/use-color-extraction';
import { motion, AnimatePresence, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { toast } from 'sonner';
import { TrackOptionsSheet } from './TrackOptionsSheet';
import { AudiobookOptionsSheet } from './AudiobookOptionsSheet';
import { LyricsSyncDisplay } from './LyricsSyncDisplay';

interface NowPlayingProps {
  track?: RoxboxTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isLooping: boolean;
  onTogglePlay: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onSeek: (time: number) => void;
  onToggleLoop: () => void;
  onClose?: () => void;
  className?: string;
}

function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

// Expandable Story Preview Card component - Spotify lyrics style docked at bottom
function StoryPreviewCard({ 
  colors, 
  onOpen,
  onExpandChange,
  isExpanded,
  title = "Story preview",
  subtitle = "Discover the full story behind this track",
  buttonText = "Go to Book",
  lyricsContent,
  isPlaying,
  onTogglePlay,
  onPrevious,
  onNext,
}: { 
  colors: { vibrant: string; dominant: string };
  onOpen: () => void;
  onExpandChange?: (expanded: boolean) => void;
  isExpanded: boolean;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  lyricsContent?: React.ReactNode;
  isPlaying?: boolean;
  onTogglePlay?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
}) {
  const cardY = useMotionValue(0);
  
  const handleTap = () => {
    if (!isExpanded) {
      hapticLight();
      onExpandChange?.(true);
    }
  };
  
  const handleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticLight();
    onExpandChange?.(false);
  };
  
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    hapticMedium();
    onOpen();
  };

  // Handle swipe down to collapse when expanded
  const handleCardDragEnd = (_: any, info: PanInfo) => {
    if (isExpanded && (info.offset.y > 50 || info.velocity.y > 300)) {
      hapticLight();
      onExpandChange?.(false);
    }
    cardY.set(0);
  };

  // Collapsed state - just a small pill
  if (!isExpanded) {
    return (
      <motion.div
        onClick={handleTap}
        className="w-full rounded-t-3xl cursor-pointer flex-shrink-0"
        style={{ 
          backgroundColor: colors.vibrant,
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}
      >
        <div className="px-5 py-4">
          <p className="text-white/90 text-sm font-medium">{title}</p>
        </div>
      </motion.div>
    );
  }

  // Expanded state - fills remaining space
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      drag="y"
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.5 }}
      onDragEnd={handleCardDragEnd}
      className="flex-1 flex flex-col min-h-0 rounded-t-3xl overflow-hidden"
      style={{ 
        backgroundColor: colors.vibrant,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        y: cardY
      }}
    >
      {/* Header */}
      <div className="px-5 pt-4 pb-2 flex-shrink-0">
        <div className="flex justify-center mb-2">
          <div className="w-10 h-1 rounded-full bg-white/30" />
        </div>
        <div className="flex items-center justify-between">
          <p className="text-white/90 text-sm font-medium">{title}</p>
          <button 
            onClick={handleCollapse}
            className="p-1 -mr-1 text-white/70 active:scale-90 transition-transform"
          >
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Content - scrollable */}
      <div className="flex-1 overflow-y-auto scrollbar-hide px-5 min-h-0">
        {lyricsContent ? (
          lyricsContent
        ) : (
          <p className="text-white font-bold text-xl leading-snug mt-1 line-clamp-3">
            {subtitle}
          </p>
        )}
      </div>
      
      {/* Footer button + playback controls */}
      <div className="px-5 pt-3 pb-4 flex-shrink-0 flex items-center justify-between">
        <button 
          onClick={handleButtonClick}
          className="text-sm font-semibold active:opacity-70 transition-opacity text-white dark:text-white"
        >
          {buttonText}
        </button>
        
        {/* Mini playback controls */}
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); hapticLight(); onPrevious?.(); }}
            className="p-2 text-white/80 active:scale-90 transition-transform"
          >
            <SkipBack className="w-5 h-5" fill="currentColor" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); hapticMedium(); onTogglePlay?.(); }}
            className="p-2 text-white active:scale-90 transition-transform"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" fill="currentColor" />
            ) : (
              <Play className="w-6 h-6" fill="currentColor" />
            )}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); hapticLight(); onNext?.(); }}
            className="p-2 text-white/80 active:scale-90 transition-transform"
          >
            <SkipForward className="w-5 h-5" fill="currentColor" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function NowPlaying({
  track,
  isPlaying,
  currentTime,
  duration,
  isLooping,
  onTogglePlay,
  onPrevious,
  onNext,
  onSeek,
  onToggleLoop,
  onClose,
  className,
}: NowPlayingProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const isV2Route = location.pathname === '/v2' || location.pathname.startsWith('/v2/');
  const { currentAudiobookPage, currentAudiobook, playingType, setNowPlayingOpen, canSkipNext, onPaywallHit } = useRoxboxPlayerContext();
  const videoRef = useRef<HTMLVideoElement>(null);
  const lastSyncTime = useRef<number>(0);
  const [isDragging, setIsDragging] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [storyPreviewExpanded, setStoryPreviewExpanded] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const hasVideo = isMobile && !!track?.video_url;

  // Determine display values
  const isAudiobookMode = playingType === 'audiobook' && currentAudiobookPage;
  const displayTitle = isAudiobookMode ? `Page ${currentAudiobookPage.pageNumber}` : track?.name || '';
  
  const getAudiobookSubtitle = () => {
    if (!currentAudiobookPage) return '';
    const words = currentAudiobookPage.bookTitle.trim().split(/\s+/).slice(0, 4);
    return words.join(' ');
  };
  const displayArtist = isAudiobookMode ? getAudiobookSubtitle() : track?.artist || '';
  
  // Dynamic color extraction
  const coverImage = isAudiobookMode 
    ? currentAudiobookPage?.imageUrl 
    : track?.cover_image_url;
  const { colors } = useColorExtraction(coverImage);

  // Fetch linked book images for cycling
  const { data: linkedBookImages } = useQuery({
    queryKey: ['linkedBookImages', track?.book_id],
    queryFn: async () => {
      if (!track?.book_id) return [];
      const { data, error } = await supabase
        .from('books')
        .select('page_images, cover_image_url')
        .eq('id', track.book_id)
        .single();
      
      if (error || !data) return [];
      
      const pageImages = data.page_images as Array<{ url?: string; imageUrl?: string; image_url?: string }> | null;
      const images: string[] = [];
      
      if (data.cover_image_url) images.push(data.cover_image_url);
      if (pageImages && Array.isArray(pageImages)) {
        pageImages.forEach((page) => {
          const url = page.url || page.imageUrl || page.image_url;
          if (url) images.push(url);
        });
      }
      return images;
    },
    enabled: !!track?.book_id && !isAudiobookMode,
    staleTime: 5 * 60 * 1000,
  });

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());
  const hasLinkedImages = linkedBookImages && linkedBookImages.length > 1;

  // Preload images
  useEffect(() => {
    if (!linkedBookImages || linkedBookImages.length === 0) return;
    linkedBookImages.forEach((url) => {
      const img = new Image();
      img.onload = () => setLoadedImages((prev) => new Set(prev).add(url));
      img.src = url;
    });
  }, [linkedBookImages]);

  // Cycle images
  useEffect(() => {
    if (!hasLinkedImages || !isPlaying) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => {
        let nextIndex = (prev + 1) % linkedBookImages.length;
        for (let i = 0; i < linkedBookImages.length; i++) {
          if (loadedImages.has(linkedBookImages[nextIndex])) return nextIndex;
          nextIndex = (nextIndex + 1) % linkedBookImages.length;
        }
        return prev;
      });
    }, 8000);
    return () => clearInterval(interval);
  }, [hasLinkedImages, isPlaying, linkedBookImages?.length, loadedImages]);

  useEffect(() => {
    setCurrentImageIndex(0);
    setLoadedImages(new Set());
  }, [track?.id]);

  const fallbackImage = isAudiobookMode 
    ? currentAudiobookPage?.imageUrl || '' 
    : track?.cover_image_url || '';

  // Navigation handlers
  const handleOpenReader = () => {
    if (!currentAudiobook || !currentAudiobookPage) return;
    setNowPlayingOpen(false);
    // Use V2 route when on V2 or when it's a Builder audiobook
    if (isV2Route || currentAudiobook.builderBookId) {
      navigate(`/v2`, { state: { openBookId: String(currentAudiobook.builderBookId || currentAudiobook.id) } });
    } else {
      navigate(`/read/${currentAudiobook.id}?page=${currentAudiobookPage.pageNumber}`, {
        state: { returnPath: '/jukebox' }
      });
    }
  };

  const handleOpenLinkedBook = () => {
    if (!track?.book_id) return;
    setNowPlayingOpen(false);
    navigate(`/?book=${track.book_id}`, { state: { from: '/jukebox' } });
  };

  // Like functionality
  const { data: likedTrackIds = [] } = useQuery({
    queryKey: ['likedTracks', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('liked_tracks')
        .select('track_id')
        .eq('user_id', user.id);
      if (error) throw error;
      return data.map(lt => lt.track_id);
    },
    enabled: !!user,
  });

  const isLiked = track ? likedTrackIds.includes(track.id) : false;

  const likeMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) return;
      const { error } = await supabase.from('liked_tracks').insert({ user_id: user.id, track_id: trackId });
      if (error) {
        console.error('Like failed:', error);
        throw error;
      }
    },
    onMutate: async (trackId: string) => {
      await queryClient.cancelQueries({ queryKey: ['likedTracks', user?.id] });
      const prev = queryClient.getQueryData<string[]>(['likedTracks', user?.id]) ?? [];
      queryClient.setQueryData(['likedTracks', user?.id], [...prev, trackId]);
      return { prev };
    },
    onError: (_err, _trackId, context) => {
      queryClient.setQueryData(['likedTracks', user?.id], context?.prev);
      toast.error('Could not like track');
    },
  });

  const unlikeMutation = useMutation({
    mutationFn: async (trackId: string) => {
      if (!user) return;
      const { error } = await supabase.from('liked_tracks').delete().eq('user_id', user.id).eq('track_id', trackId);
      if (error) {
        console.error('Unlike failed:', error);
        throw error;
      }
    },
    onMutate: async (trackId: string) => {
      await queryClient.cancelQueries({ queryKey: ['likedTracks', user?.id] });
      const prev = queryClient.getQueryData<string[]>(['likedTracks', user?.id]) ?? [];
      queryClient.setQueryData(['likedTracks', user?.id], prev.filter(id => id !== trackId));
      return { prev };
    },
    onError: (_err, _trackId, context) => {
      queryClient.setQueryData(['likedTracks', user?.id], context?.prev);
      toast.error('Could not unlike track');
    },
  });

  const toggleLike = () => {
    if (!user || !track) return;
    hapticLight();
    if (isLiked) unlikeMutation.mutate(track.id);
    else likeMutation.mutate(track.id);
  };

  // Video sync
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasVideo) return;
    if (isPlaying) video.play().catch(() => {});
    else video.pause();
  }, [isPlaying, hasVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasVideo) return;
    const timeDiff = Math.abs(video.currentTime - currentTime);
    if (timeDiff > 0.5 && currentTime !== lastSyncTime.current) {
      video.currentTime = currentTime;
      lastSyncTime.current = currentTime;
    }
  }, [currentTime, hasVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (video && hasVideo) {
      video.currentTime = 0;
      if (isPlaying) video.play().catch(() => {});
    }
  }, [track?.id, hasVideo]);

  // Progress bar interaction - supports both click and touch drag
  const handleProgressInteraction = (clientX: number, element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const percent = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    onSeek(percent * duration);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    handleProgressInteraction(e.clientX, e.currentTarget);
  };

  const handleProgressTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsDragging(true);
    hapticLight();
    handleProgressInteraction(e.touches[0].clientX, e.currentTarget);
  };

  const handleProgressTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    e.stopPropagation();
    handleProgressInteraction(e.touches[0].clientX, e.currentTarget);
  };

  const handleProgressTouchEnd = () => {
    setIsDragging(false);
  };

  if (!track && !currentAudiobookPage) {
    return (
      <div className={cn("flex flex-col items-center justify-center h-full text-white/50", className)}>
        <p className="text-lg">Select a track to play</p>
      </div>
    );
  }

  // Share handler
  const handleShare = async () => {
    const shareTitle = displayTitle;
    const shareArtist = displayArtist;
    
    // Use App Store link for sharing
    const appStoreUrl = 'https://apps.apple.com/gb/app/buxbox/id6758412710';
    const shareUrl = isAudiobookMode && currentAudiobook
      ? `${window.location.origin}/jukebox?audiobook=${currentAudiobook.id}&page=${currentAudiobookPage?.pageNumber || 1}`
      : track
      ? `${window.location.origin}/jukebox?track=${track.id}`
      : window.location.href;

    const shareText = `Listen to ${shareTitle}${shareArtist ? ` by ${shareArtist}` : ''} on BuxBox`;

    hapticLight();

    // Try Despia native share first, then Web Share API, then clipboard
    if (typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().includes('despia')) {
      try {
        const { default: despia } = await import('despia-native');
        despia(`shareapp://message?=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`);
        return;
      } catch {
        // Not on native, continue
      }
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: `${shareText} ${appStoreUrl}`,
          url: shareUrl,
        });
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied!');
        }
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied!');
    }
  };

  // Swipe to dismiss
  const y = useMotionValue(0);
  const opacity = useTransform(y, [0, 300], [1, 0.3]);
  const scale = useTransform(y, [0, 300], [1, 0.9]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.y > 100 || info.velocity.y > 500) {
      hapticLight();
      onClose?.();
      setNowPlayingOpen(false);
    }
  };

  // Mobile full-screen Now Playing
  if (isMobile) {
    return (
      <motion.div 
        style={{ y, opacity, scale }}
        drag="y"
        dragConstraints={{ top: 0, bottom: 0 }}
        dragElastic={{ top: 0, bottom: 0.7 }}
        onDragEnd={handleDragEnd}
        className={cn("fixed inset-0 z-50 flex flex-col touch-pan-x", className)}
      >
        {/* Solid black base to prevent bleed-through */}
        <div className="absolute inset-0 -z-20 bg-black" />
        {/* Dynamic background */}
        <div 
          className="absolute inset-0 -z-10"
          style={{
            background: `linear-gradient(180deg, ${colors.dominant} 0%, #000000 100%)`,
          }}
        />
        
        {/* Header with navigation */}
        <div 
          className="flex items-center justify-between px-4"
          style={{ paddingTop: 'calc(var(--safe-top) + 0.75rem)' }}
        >
          <button 
            onClick={() => {
              hapticLight();
              onClose?.();
              setNowPlayingOpen(false);
            }}
            className="p-2 text-white active:scale-90 transition-transform"
          >
            <ChevronDown className="w-7 h-7" />
          </button>
          
          <span className="text-white text-sm font-medium truncate max-w-[60%]">
            {displayTitle}
          </span>
          
          <button 
            onClick={() => {
              hapticLight();
              setOptionsOpen(true);
            }}
            className="p-2 text-white active:scale-90 transition-transform"
          >
            <MoreHorizontal className="w-6 h-6" />
          </button>
        </div>
        
        {/* Options Sheet - different for tracks vs audiobooks */}
        {isAudiobookMode ? (
          <AudiobookOptionsSheet 
            open={optionsOpen} 
            onOpenChange={setOptionsOpen} 
            audiobook={currentAudiobook ? {
              id: currentAudiobook.id,
              title: currentAudiobook.title,
              coverUrl: currentAudiobook.coverUrl
            } : null}
            currentPage={currentAudiobookPage ? {
              pageNumber: currentAudiobookPage.pageNumber,
              imageUrl: currentAudiobookPage.imageUrl,
              bookTitle: currentAudiobookPage.bookTitle
            } : null}
            onSubscribe={onPaywallHit}
          />
        ) : (
          <TrackOptionsSheet 
            open={optionsOpen} 
            onOpenChange={setOptionsOpen} 
            track={track || null}
            onSubscribe={onPaywallHit}
          />
        )}

        {/* Album Art - transitions from 1:1 to 4:3 when story expands */}
        <motion.div 
          className="flex items-start justify-center px-4 overflow-hidden"
          animate={{
            flex: storyPreviewExpanded ? '0 0 auto' : '1 1 0%',
            paddingTop: storyPreviewExpanded ? '0.5rem' : '1.5rem',
            paddingBottom: storyPreviewExpanded ? '0' : '1.5rem',
          }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
        >
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ 
              scale: 1, 
              opacity: 1,
              aspectRatio: storyPreviewExpanded ? '4/3' : '1/1',
            }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              "rounded-xl overflow-hidden shadow-2xl mb-4",
              storyPreviewExpanded ? "w-full max-h-[30vh]" : "w-full max-w-[85vw] aspect-square"
            )}
            style={{ boxShadow: `0 25px 50px -12px ${colors.dominant}80` }}
          >
            {hasVideo && track ? (
              <video
                ref={videoRef}
                src={track.video_url!}
                className="w-full h-full object-cover"
                loop={isLooping}
                muted
                playsInline
              />
            ) : (
              <img
                src={fallbackImage}
                alt={displayTitle}
                className="w-full h-full object-cover"
              />
            )}
          </motion.div>
        </motion.div>

        {/* Bottom Controls */}
        <div 
          className="px-8 transition-colors duration-300"
          style={{ 
            paddingBottom: (track?.book_id || isAudiobookMode) ? '0' : 'calc(env(safe-area-inset-bottom, 0px) + 2rem)',
            backgroundColor: storyPreviewExpanded ? colors.vibrant : 'transparent'
          }}
        >
          {/* Track Info - hidden when story preview expanded */}
          {!storyPreviewExpanded && (
            <div className="flex items-center justify-between mb-6">
              <div className="flex-1 min-w-0 mr-4">
                <h2 className="text-white text-xl font-bold truncate">{displayTitle}</h2>
                <p className="text-white/60 text-base truncate">{displayArtist || 'Unknown Artist'}</p>
              </div>
              <div className="flex items-center gap-1">
                {user && !isAudiobookMode && track && (
                  <button
                    onClick={toggleLike}
                    className={cn(
                      "p-2 transition-all active:scale-90",
                      isLiked ? "text-primary" : "text-white/50"
                    )}
                  >
                    <Heart className="w-6 h-6" fill={isLiked ? "currentColor" : "none"} />
                  </button>
                )}
                <button
                  onClick={handleShare}
                  className="p-2 text-white/50 transition-all active:scale-90"
                >
                  <PiShareFatFill className="w-6 h-6" />
                </button>
              </div>
            </div>
          )}

          {/* Progress Bar - Draggable on mobile, hidden when story preview expanded */}
          {!storyPreviewExpanded && (
            <div className="mb-3">
              <div 
                ref={progressRef}
                className="relative w-full h-6 flex items-center cursor-pointer group touch-none"
                onClick={handleProgressClick}
                onTouchStart={handleProgressTouchStart}
                onTouchMove={handleProgressTouchMove}
                onTouchEnd={handleProgressTouchEnd}
              >
                <div className="absolute inset-x-0 h-1 bg-white/20 rounded-full">
                  <div
                    className="absolute top-0 left-0 h-full bg-white rounded-full"
                    style={{ width: `${progress}%` }}
                  />
                </div>
                <div
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg transition-opacity",
                    isDragging ? "opacity-100 scale-125" : "opacity-100"
                  )}
                  style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)' }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-white/50 font-medium">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>
          )}

          {/* Main Controls - hidden when story preview expanded */}
          {!storyPreviewExpanded && (
            <div className="flex items-center justify-between py-2">
              {/* Loop */}
              <button
                onClick={() => {
                  hapticLight();
                  onToggleLoop();
                }}
                className={cn(
                  "p-3 transition-all active:scale-90",
                  isLooping ? "text-primary" : "text-white/50"
                )}
              >
                <Repeat className="w-5 h-5" />
              </button>

              {/* Skip/Play Controls */}
              <div className="flex items-center gap-6">
                <button
                  onClick={() => {
                    hapticMedium();
                    onPrevious();
                  }}
                  className="text-white active:scale-90 transition-transform p-2"
                >
                  <SkipBack className="w-8 h-8" fill="currentColor" />
                </button>

                <button
                  onClick={() => {
                    hapticMedium();
                    onTogglePlay();
                  }}
                  className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-black active:scale-95 transition-transform shadow-lg"
                >
                  {isPlaying ? (
                    <Pause className="w-7 h-7" fill="currentColor" />
                  ) : (
                    <Play className="w-7 h-7 ml-1" fill="currentColor" />
                  )}
                </button>

                <button
                  onClick={() => {
                    hapticMedium();
                    if (canSkipNext) onNext();
                    else onPaywallHit?.();
                  }}
                  className={cn(
                    "active:scale-90 transition-transform p-2",
                    canSkipNext ? "text-white" : "text-white/30"
                  )}
                >
                  <SkipForward className="w-8 h-8" fill="currentColor" />
                </button>
              </div>

              {/* Placeholder for symmetry or future feature */}
              <div className="w-11 h-11" />
            </div>
          )}

        </div>
        
        {/* Story Preview Card - Spotify Lyrics style docked at bottom */}
        {track?.book_id && !isAudiobookMode && (
          <StoryPreviewCard 
            colors={colors} 
            onOpen={handleOpenLinkedBook}
            onExpandChange={setStoryPreviewExpanded}
            isExpanded={storyPreviewExpanded}
          />
        )}
        
        {/* Continue in Reader for audiobook mode - with karaoke lyrics */}
        {isAudiobookMode && currentAudiobookPage && (
          <StoryPreviewCard 
            colors={colors} 
            onOpen={handleOpenReader}
            onExpandChange={setStoryPreviewExpanded}
            isExpanded={storyPreviewExpanded}
            title="Story"
            subtitle={`Page ${currentAudiobookPage?.pageNumber} of ${currentAudiobook?.totalPages}`}
            buttonText="Open in Reader"
            isPlaying={isPlaying}
            onTogglePlay={onTogglePlay}
            onPrevious={onPrevious}
            onNext={onNext}
            lyricsContent={
              <LyricsSyncDisplay
                bookId={currentAudiobookPage.bookId}
                pageNumber={currentAudiobookPage.pageNumber}
                bookTitle={currentAudiobook?.title}
                content={currentAudiobookPage.fullContent || currentAudiobookPage.bookTitle}
                currentTime={currentTime}
                isPlaying={isPlaying}
                dominantColor={colors.dominant}
                onSeek={onSeek}
                inlineWordTimings={currentAudiobookPage.wordTimings}
              />
            }
          />
        )}
        
        {/* Safe area bottom spacer removed - iOS Safari handles this natively */}
      </motion.div>
    );
  }

  // Desktop should use DesktopPlayerBar instead - this component is mobile-only
  // If this is reached on desktop, just return null (desktop uses persistent bar)
  return null;
}
